/**
 * A klankvoet from an annotation, e.g. `{ klanken: ['g', 'e', 'r'], stom: true }` for the 'ger' in 'tijger'.
 * Klanken keep their spelling rule prefix: `+k` (dubbelzetter), `-aa` (tekendief) or `|d` (langermaakregel).
 */
export interface TAnnotatedKlankvoet {
  klanken: string[];
  /** A stomme klankvoet: its klinker is a sjwa. */
  stom: boolean;
}

/**
 * Reads RID's word annotation (`RIDWoord.annotatie`): klanken separated by `/`, where `>` starts a normale klankvoet,
 * `<` a stomme klankvoet, `_` marks a compound boundary, and `+`, `-` and `|` mark spelling rules.
 */
export class AnnotationUtils {
  /** Splits an annotation like `>t/ij/<g/e/r` into klankvoeten. Without markers, the whole word is one klankvoet. */
  public static parse(annotatie: string): TAnnotatedKlankvoet[] {
    const voeten: TAnnotatedKlankvoet[] = [];

    for (const raw of annotatie.split('/')) {
      let token = raw.trim().replaceAll('_', '');
      let marker: string | undefined;
      while (token.startsWith('>') || token.startsWith('<')) {
        marker = token[0];
        token = token.slice(1);
      }
      if (!token) continue;

      if (marker || voeten.length === 0) voeten.push({ klanken: [], stom: marker === '<' });
      voeten[voeten.length - 1].klanken.push(token);
    }

    // a dubbelzetter belongs to the klankvoet after it: 'muggen' reads as 'mu' + 'ggen'
    for (let i = 0; i + 1 < voeten.length; i++) {
      const left = voeten[i].klanken;
      if (left.length > 1 && left[left.length - 1].startsWith('+')) voeten[i + 1].klanken.unshift(left.pop()!);
    }

    return voeten;
  }

  /** The klankvoet as written, e.g. `'ggen'` for `+g/e/n`. */
  public static write(voet: TAnnotatedKlankvoet): string {
    return this.writeKlanken(voet).join('');
  }

  /** The klanken as written, e.g. `['gg', 'e', 'n']` for `+g/e/n`. */
  public static writeKlanken(voet: TAnnotatedKlankvoet): string[] {
    return voet.klanken.map((klank) => this.writeKlank(klank));
  }

  /** A single klank as written, with its spelling rule applied: `+g` -> `gg`, `-aa` -> `a`, `|d` -> `d`. */
  public static writeKlank(klank: string): string {
    if (klank.startsWith('+')) return klank.slice(1).repeat(2); // dubbelzetter: the letter doubles
    if (klank.startsWith('|')) return klank.slice(1);
    if (klank.startsWith('-') && klank.length > 1) return klank.slice(1, -1); // tekendief: the last letter is dropped
    return klank;
  }

  public static isKlinker(klank: string): boolean {
    return /[aeiouy]/.test(this.writeKlank(klank));
  }
}
